// Demo mode utilities for masking sensitive data during client demonstrations

/**
 * Check if current user is in demo mode
 * @returns {boolean} True if user has demo role
 */
export const isDemoMode = () => {
  try {
    // Get user from localStorage (adjust this based on your auth implementation)
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role === 'demo';
  } catch (error) {
    console.error('Error checking demo mode:', error);
    return false;
  }
};

/**
 * Get demo-appropriate Brazilian names
 * @returns {Array} Array of demo names (50% male, 50% female)
 */
const getDemoNames = () => [
  // Female names (50)
  'Ana Silva',
  'Maria Oliveira',
  'Sofia Rodrigues',
  'Beatriz Lima',
  'Isabella Ferreira',
  'Laura Carvalho',
  'Valentina Gomes',
  'Helena Martins',
  'Alice Barbosa',
  'Manuela Castro',
  'Julia Santos',
  'Camila Costa',
  'Larissa Pereira',
  'Caroline Souza',
  'Mariana Alves',
  'Fernanda Ribeiro',
  'Rafaela Dias',
  'Vitória Moreira',
  'Gabriela Rocha',
  'Eduarda Barbosa',
  'Nicole Ferreira',
  'Yasmin Silva',
  'Emanuelly Santos',
  'Giovanna Oliveira',
  'Lívia Costa',
  'Lara Rodrigues',
  'Melissa Pereira',
  'Catarina Lima',
  'Isadora Souza',
  'Amanda Alves',
  'Rebeca Carvalho',
  'Letícia Ribeiro',
  'Antonella Gomes',
  'Cecília Dias',
  'Sarah Martins',
  'Esther Moreira',
  'Mirella Rocha',
  'Clarice Barbosa',
  'Pietra Castro',
  'Nina Silva',
  'Luna Santos',
  'Maitê Oliveira',
  'Stella Costa',
  'Aurora Rodrigues',
  'Maya Pereira',
  'Elisa Lima',
  'Milena Souza',
  'Bianca Ferreira',
  'Agatha Alves',
  'Isis Carvalho',
  // Male names (50)
  'João Santos',
  'Pedro Costa',
  'Lucas Pereira',
  'Gabriel Souza',
  'Miguel Alves',
  'Davi Ribeiro',
  'Arthur Dias',
  'Heitor Moreira',
  'Theo Rocha',
  'Lorenzo Silva',
  'Rafael Santos',
  'Bruno Oliveira',
  'Felipe Costa',
  'Mateus Rodrigues',
  'Daniel Pereira',
  'Ricardo Lima',
  'Diego Souza',
  'Gustavo Ferreira',
  'Henrique Alves',
  'Vinícius Carvalho',
  'Leonardo Ribeiro',
  'Samuel Gomes',
  'Enzo Dias',
  'Nicolas Martins',
  'Benjamin Moreira',
  'Antônio Rocha',
  'Caleb Barbosa',
  'Emanuel Castro',
  'Joaquim Silva',
  'Murilo Santos',
  'Eduardo Oliveira',
  'Cauã Costa',
  'Isaac Rodrigues',
  'Caio Pereira',
  'Noah Lima',
  'Ryan Souza',
  'Anthony Ferreira',
  'José Alves',
  'Yuri Carvalho',
  'Oliver Ribeiro',
  'Otávio Gomes',
  'Ian Dias',
  'Pietro Martins',
  'Levi Moreira',
  'Nathan Rocha',
  'Bryan Barbosa',
  'Apollo Castro',
  'Bento Silva',
  'Noah Santos',
  'Gael Oliveira'
];

/**
 * Mask patient name with consistent demo name
 * @param {string} realName - The real patient name
 * @param {string|number} patientId - Patient ID for consistent mapping
 * @returns {string} Masked demo name or real name if not in demo mode
 */
export const maskPatientName = (realName, patientId) => {
  if (!isDemoMode()) {
    return realName;
  }
  
  if (!realName || !patientId) {
    return realName;
  }
  
  const demoNames = getDemoNames();
  // Use patient ID to ensure consistent mapping
  const nameIndex = Math.abs(String(patientId).split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % demoNames.length;
  
  return demoNames[nameIndex];
};

/**
 * Mask email address for demo mode
 * @param {string} realEmail - The real email address
 * @param {string|number} patientId - Patient ID for consistent mapping
 * @returns {string} Masked demo email or real email if not in demo mode
 */
export const maskEmail = (realEmail, patientId) => {
  if (!isDemoMode()) {
    return realEmail;
  }
  
  if (!realEmail) {
    return realEmail;
  }
  
  // Generate consistent demo email based on patient ID
  const emailDomains = ['demo.com', 'exemplo.com', 'teste.com'];
  const domainIndex = Math.abs(String(patientId).charCodeAt(0)) % emailDomains.length;
  
  return `demo.paciente${patientId}@${emailDomains[domainIndex]}`;
};

/**
 * Mask phone number for demo mode
 * @param {string} realPhone - The real phone number
 * @returns {string} Masked demo phone or real phone if not in demo mode
 */
export const maskPhone = (realPhone) => {
  if (!isDemoMode()) {
    return realPhone;
  }
  
  if (!realPhone) {
    return realPhone;
  }
  
  return '(11) 99999-9999';
};

/**
 * Mask address for demo mode
 * @param {string} realAddress - The real address
 * @param {string|number} patientId - Patient ID for consistent mapping
 * @returns {string} Masked demo address or real address if not in demo mode
 */
export const maskAddress = (realAddress, patientId) => {
  if (!isDemoMode()) {
    return realAddress;
  }
  
  if (!realAddress) {
    return realAddress;
  }
  
  const demoStreets = [
    'Rua das Flores',
    'Avenida Demo', 
    'Rua Exemplo',
    'Avenida Teste',
    'Rua da Demonstração'
  ];
  
  const streetIndex = Math.abs(String(patientId).charCodeAt(0)) % demoStreets.length;
  const number = (Math.abs(String(patientId).charCodeAt(1)) % 999) + 1;
  
  return `${demoStreets[streetIndex]}, ${number}`;
};

/**
 * Get demo mode indicator component props
 * @returns {Object} Props for demo mode indicator
 */
export const getDemoModeIndicator = () => {
  if (!isDemoMode()) {
    return null;
  }
  
  return {
    text: 'MODO DEMONSTRAÇÃO',
    className: 'bg-orange-500 text-white px-3 py-1 text-xs font-medium rounded-full',
    icon: '🎭'
  };
};

/**
 * Log demo mode activity (for debugging)
 * @param {string} component - Component name
 * @param {string} action - Action performed
 */
export const logDemoActivity = (component, action) => {
  if (isDemoMode() && process.env.NODE_ENV === 'development') {
    console.log(`🎭 Demo Mode - ${component}: ${action}`);
  }
};
